import React, { useCallback, useState } from 'react';
import {
  createNewCustomer,
  dispatchCustomerError,
  dispatchCustomerSuccess,
  updateCustomerData
} from '../../../services/customerServices';
import { isEmpty } from '../../../helpers/common';
import Button from '../../button';
import { captureException } from '../../../services/logService';
import { useHistory } from 'react-router-dom';
import { Formation, FormButton, Label, ModalBlock } from './styles';
import { BUTTON_TYPE } from '../../../global/constants';
import ModalHeader from '../modalHeader';
import Input from '../../input';
import TextArea from '../../input/textArea';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ROUTES } from '../../../routes/urls';

interface Props {
  loadData?: () => void;
  handleCloseModal: () => void;
}

const CustomerModal: React.FC<Props> = (props) => {
  const { handleCloseModal, loadData } = props;
  //State
  const [loading, setLoading] = useState(false);

  //MobX store
  const { customerStore } = rootStore;
  const { newCustomer } = customerStore;
  const { companyName, address } = newCustomer || {};

  //Other variable
  const history = useHistory();

  const onUpdateInputValue = useCallback(
    (propsName: string, value: string) => {
      customerStore.updateCustomerDetail(propsName, value);
    },
    [customerStore]
  );

  const handleSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        let response;
        if (isEmpty(companyName)) {
          dispatchCustomerError('Please enter company name.');
          return;
        }

        if (!isEmpty(newCustomer?.id)) {
          const updatePayload = {
            CompanyName: companyName,

            Address: address
          };
          response = await updateCustomerData(newCustomer?.id, updatePayload);
          if (response) dispatchCustomerSuccess('Customer details updated successfully!');
        } else {
          const createPayload = {
            CompanyName: companyName,
            Address: address || ''
          };
          response = await createNewCustomer(createPayload);
          if (response) {
            dispatchCustomerSuccess('Customer added successfully!');
            history.push(ROUTES.CUSTOMER_DETAILS.replace(':id', response['_id'] || ''));
          }
        }
        if (response) {
          handleCloseModal();
          customerStore.clearCustomerDetail();
          if (loadData) loadData();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [companyName, address, newCustomer?.id, customerStore, history, handleCloseModal, loadData]
  );

  return (
    <ModalBlock onSubmit={handleSubmit}>
      <ModalHeader
        title={isEmpty(newCustomer?.id) ? 'Add new customer' : 'Update Customer Data'}
        onClose={handleCloseModal}
      />
      <Formation>
        <Input
          label={
            <Label>
              Company name<span>*</span>
            </Label>
          }
          value={companyName}
          onChange={(e) => {
            onUpdateInputValue('companyName', e.target.value);
          }}
          placeHolder='Enter company name '
          type='text'
          autoFocus
        />
        <TextArea
          height={'78px'}
          label='Address'
          placeHolder={`Enter company's address`}
          value={address || ''}
          onChange={(e) => {
            onUpdateInputValue('address', e.target.value);
          }}
        />
      </Formation>
      <FormButton>
        <Button title='Cancel' onClick={handleCloseModal} variant={BUTTON_TYPE.SECONDARY} buttonType='button' />
        <Button
          buttonType='submit'
          title={isEmpty(newCustomer?.id) ? 'Add Customer' : 'Update Customer'}
          variant={BUTTON_TYPE.PRIMARY}
          isLoading={loading}
        />
      </FormButton>
    </ModalBlock>
  );
};

export default observer(CustomerModal);
